import { getBasemap } from '@mighty-map/map-engine';
import type { GlobeEngine, GlobeTerrain } from '@mighty-map/map-engine/cesium';
import { useEffect, useRef, useState } from 'react';
import { getDataToken } from '../../state/dataToken';
import { selectActiveTheme, useMapStore } from '../../state/mapStore';
import { useViewMode } from '../../state/viewMode';
import './globe.css';
import { globeCameraFor, registerViewEngine } from './viewSync';

type Status = { kind: 'loading' } | { kind: 'ready' } | { kind: 'failed'; message: string };

/**
 * The Globe view: production's 3D scene of the same place. Made the first time Globe is entered (the renderer is a
 * large chunk, loaded then) and kept after, hidden while another view is on screen, because a remount is slow.
 * `active` says whether it is the view on screen; a hidden Globe stops drawing and streaming.
 */
export function GlobeView({ active, onEngine }: { active: boolean; onEngine: (engine: GlobeEngine | null) => void }) {
  const container = useRef<HTMLDivElement>(null);
  const [engine, setEngine] = useState<GlobeEngine | null>(null);
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  const [terrain, setTerrain] = useState<GlobeTerrain>('flat');
  const [drapePending, setDrapePending] = useState(0);
  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    let cancelled = false;
    let created: GlobeEngine | null = null;
    import('@mighty-map/map-engine/cesium')
      .then(({ createCesiumEngine }) => {
        const el = container.current;
        const s = useMapStore.getState();
        const dataUrl = s.config?.project.data_api_url;
        if (cancelled || !el || !dataUrl) return;
        const shared = useViewMode.getState().camera;
        created = createCesiumEngine(el, {
          getToken: getDataToken,
          dataUrl,
          basemapId: s.basemapId,
          assetsUrl: `${import.meta.env.BASE_URL}globe-assets/`,
          ...(shared ? { camera: globeCameraFor(shared, selectActiveTheme(s)?.camera.globe) } : {}),
          onBasemapFallback: (fromId, toId) => {
            const store = useMapStore.getState();
            store.setBasemap(toId);
            store.setNotice(
              `${getBasemap(fromId).title} can't be reached from this network; showing ${getBasemap(toId).title}.`,
            );
          },
        });
        const globe = created;
        globe.onCameraChange((c) => {
          if (activeRef.current) useMapStore.getState().setZoom(c.zoom);
        });
        globe.onClick((e) => {
          // While a tool is drawing (Measure, Sketch), clicks are its vertices, not identify (as on the map).
          if (globe.draw.isActive() || !activeRef.current) return;
          const top = e.features[0];
          useMapStore
            .getState()
            .setPopup(top ? { layerId: top.layerId, properties: top.properties, point: e.point } : null);
        });
        globe.onTerrainChange(setTerrain);
        globe.onDrapeProgress(setDrapePending);
        globe.setActive(activeRef.current);
        setTerrain(globe.terrain);
        registerViewEngine('globe', globe);
        // A development handle for looking at the live engine from the console or a screenshot script.
        if (import.meta.env.DEV) (window as { __mmGlobe?: GlobeEngine }).__mmGlobe = globe;
        setEngine(globe);
        setStatus({ kind: 'ready' });
        onEngine(globe);
      })
      .catch((e: unknown) => {
        if (!cancelled) setStatus({ kind: 'failed', message: e instanceof Error ? e.message : String(e) });
      });
    const onResize = (): void => created?.resize();
    window.addEventListener('resize', onResize);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', onResize);
      registerViewEngine('globe', null);
      onEngine(null);
      created?.destroy();
    };
  }, [onEngine]);

  useEffect(() => {
    engine?.setActive(active);
  }, [engine, active]);

  return (
    <div
      ref={container}
      className="mm-globe"
      style={{ visibility: active ? 'visible' : 'hidden' }}
      role="region"
      aria-label="Globe"
      aria-hidden={!active}
      data-terrain={terrain}
      data-drape-pending={drapePending}
    >
      {status.kind === 'loading' && (
        <div className="mm-globe-status" role="status">
          Loading the globe…
        </div>
      )}
      {status.kind === 'failed' && (
        <div className="mm-globe-status" role="alert">
          The globe could not start here: {status.message}
        </div>
      )}
      {status.kind === 'ready' && terrain === 'flat' && (
        <div className="mm-globe-chip" role="status" title="The elevation service has not answered yet">
          Terrain: not available yet
        </div>
      )}
    </div>
  );
}
